<template>
  <div class="kvass-selector" :readonly="readonly">
    <div v-if="type === 'default'" @click="open" class="kvass-selector__input">
      <slot v-if="valueComp.length" name="items" :items="valueComp" scope="input">
        <slot v-for="item in valueComp" name="item" :item="item" scope="input" />
      </slot>
      <slot v-if="!hasValue" name="empty" scope="input" />
      <input
        type="text"
        class="kvass-selector__input-element"
        :value="valueComp.map(v => v.id).join('-')"
        :required="required"
      />
    </div>
    <component :is="modalWrapper" class="kvass-selector__modal-wrapper">
      <ModalComponent :show.sync="show" class="kvass-selector__modal">
        <Card theme="flat" class="kvass-selector__modal-card">
          <template #default>
            <div v-if="$slots.sidebar" class="kvass-selector__modal-sidebar">
              <slot name="sidebar" />
            </div>
            <div class="kvass-selector__modal-content">
              <div class="kvass-selector__modal-content-header">
                <strong class="kvass-selector__modal-content-title">{{ title }}</strong>
                <CheckboxComponent
                  v-if="selectAll && multiple"
                  v-model="allSelected"
                  :label="$t('selectAll')"
                />
              </div>
              <InputComponent v-if="hasSearch" class="kvass-selector__search">
                <input
                  class="elder-input__element"
                  type="text"
                  :placeholder="$t('search') + '...'"
                  v-model="defaultFilter.search"
                />
                <div class="elder-input__icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    style="width: 1rem; opacity: 0.5"
                  >
                    <path
                      d="M507.3 484.7l-141.5-141.5C397 306.8 415.1 259.7 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c51.68 0 98.85-18.96 135.2-50.15l141.5 141.5C487.8 510.4 491.9 512 496 512s8.188-1.562 11.31-4.688C513.6 501.1 513.6 490.9 507.3 484.7zM208 384C110.1 384 32 305 32 208S110.1 32 208 32S384 110.1 384 208S305 384 208 384z"
                    />
                  </svg>
                </div>
              </InputComponent>
              <LoaderComponent :value="promises.fetch" theme="default">
                <div class="kvass-selector__items">
                  <div
                    v-for="(item, index) in itemsComp"
                    :key="index"
                    class="kvass-selector__item"
                    :class="{
                      'kvass-selector__item--selected': isSelected(item),
                      'kvass-selector__item--disabled': isDisabled(item),
                    }"
                    @click="select(item)"
                  >
                    <slot name="item" :item="item" :disabled="isDisabled(item)" scope="modal" />
                  </div>
                  <slot v-if="!itemsComp.length" name="noresults" :filter="filterComp" />
                  <slot name="bottom" />
                </div>
              </LoaderComponent>
            </div>
          </template>
          <template #footer>
            <slot name="actions" />
            <ButtonComponent :label="$t('cancel')" type="button" @click="close" />
            <ButtonComponent
              type="button"
              :label="$t('confirm')"
              :icon="['fal', 'arrow-to-right']"
              :promise="promise"
              theme="primary"
              @click="confirm"
            />
          </template>
        </Card>
      </ModalComponent>
    </component>
  </div>
</template>

<script>
import { Debounce } from './utils'

import { ButtonComponent } from 'vue-elder-button'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { LoaderComponent } from 'vue-elder-loader'
import Card from '@kvass/card'

export default {
  props: {
    title: String,
    search: Boolean,
    selectAll: Boolean,
    multiple: Boolean,
    readonly: Boolean,
    required: Boolean,
    type: {
      type: String,
      enum: ['default', 'modal-only'],
      default: 'default',
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    promise: Promise,
    compare: {
      type: Function,
      default: (a, b) => a.id === b.id,
    },
    isDisabled: {
      type: Function,
      default: () => false,
    },
    value: [Object, Array, String, Number],
    items: {
      type: [Function, Array],
      required: true,
    },
    modalWrapper: {
      type: [String, Function],
      default: 'div',
    },
  },
  data() {
    return {
      show: false,
      fetchedItems: [],
      selected: [],
      promises: {
        fetch: null,
      },
      defaultFilter: {
        search: null,
      },
    }
  },
  watch: {
    show: {
      handler(val) {
        if (val)
          this.internalfetch(this.filterComp).then(() => {
            this.select(this.valueComp)
          })
        else {
          this.selected = []
        }
      },
      immediate: true,
    },
    filterComp: {
      handler: 'performQuery',
      deep: true,
    },
    promise(val) {
      if (!val) return
      val.then(() => this.close())
    },
  },
  computed: {
    filterComp() {
      return {
        ...this.defaultFilter,
        ...this.filter,
      }
    },
    valueComp: {
      get() {
        if (!this.value) return []
        return this.multiple ? this.value : [this.value]
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    itemsComp() {
      return this.items instanceof Array ? this.items : this.fetchedItems
    },
    hasValue() {
      return this.valueComp.length
    },
    hasSearch() {
      return this.search && typeof this.items === 'function'
    },
    allSelected: {
      get() {
        return this.itemsComp.every(i => this.selected.find(s => this.compare(s, i)))
      },
      set(val) {
        if (!val) return (this.selected = [])

        this.select(this.itemsComp, true)
      },
    },
  },
  methods: {
    performQuery: Debounce(function () {
      this.internalfetch(this.filterComp)
    }, 200),
    select(items, state) {
      items = items instanceof Array ? items : [items]
      items.forEach(item => {
        if (this.isDisabled(item)) return
        if ((state === undefined && this.isSelected(item)) || state === false)
          return (this.selected = this.selected.filter(i => !this.compare(i, item)))
        if ((state === undefined && !this.isSelected(item)) || state === true) {
          if (!this.multiple) this.selected = []
          this.selected.push(item)
          return
        }
      })
    },
    isSelected(item) {
      return this.selected.find(i => this.compare(i, item))
    },
    open() {
      if (this.readonly) return
      this.show = true
    },
    close() {
      this.show = false
    },
    confirm() {
      this.$emit('input', this.multiple ? this.selected : this.selected.shift() || null)
      if (!('promise' in this.$options.propsData)) this.close()
    },
    internalfetch(filter) {
      if (this.items instanceof Array) return Promise.resolve(this.items)
      return (this.promises.fetch = this.items(filter).then(data => (this.fetchedItems = data)))
    },
  },
  components: {
    Card,
    ModalComponent,
    InputComponent,
    LoaderComponent,
    ButtonComponent,
    CheckboxComponent,
  },
}
</script>

<style lang="scss">
.kvass-selector {
  &__input {
    cursor: pointer;
    position: relative;
    height: 100%;

    &-element {
      position: absolute;
      width: 100%;
      height: 100%;
      margin: 0 !important;
      top: 0;
      opacity: 0;
      pointer-events: none;
      outline: none !important;
    }

    & > * + * {
      margin-top: 1rem;
    }
  }

  &__modal-card {
    & > .card__content {
      display: flex;
    }
  }

  &__modal-sidebar {
    $spacing: 2rem;
    padding-right: $spacing;
    margin-right: $spacing;
    border-right: 1px solid var(--border-color);
  }

  &__modal-content {
    flex-grow: 1;

    &-header {
      display: flex;
      gap: 1rem;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    &-title {
      font-weight: bold;
    }
  }

  &__search {
    margin-bottom: 1rem;

    .elder-input__icon {
      padding-right: 1.1em;
    }
  }

  .card__footer {
    & > div {
      justify-content: center;
    }
  }

  &__items {
    max-height: 450px;
    overflow-y: scroll;

    @include scrollShadow();
  }

  &__item {
    cursor: pointer;
    padding: 0.5rem;
    transition: background-color 150ms ease, border-color 150ms ease;
    border-radius: var(--border-radius);
    border: 1px solid transparent;

    &:hover {
      background-color: css-alpha('border-color', 0.3);
      border-color: css-alpha('border-color', 0.3);
    }

    &--selected {
      background-color: css-alpha('primary', 0.2) !important;
      border-color: css-alpha('primary', 0.4) !important;
    }

    &--disabled {
      cursor: not-allowed;
    }

    & + * {
      margin-top: 0.2rem;
    }
  }
}

@function css-darken($key, $delta) {
  @return hsl(var(--#{$key}-h), var(--#{$key}-s), calc(var(--#{$key}-l) - #{$delta}));
}

@function css-lighten($key, $delta) {
  @return hsl(var(--#{$key}-h), var(--#{$key}-s), calc(var(--#{$key}-l) + #{$delta}));
}

@function css-alpha($key, $alpha) {
  @return hsla(var(--#{$key}-h), var(--#{$key}-s), var(--#{$key}-l), #{$alpha});
}

@mixin scrollShadow($color: white) {
  background:
		/* Shadow covers */ linear-gradient($color 30%, rgba(255, 255, 255, 0)),
    linear-gradient(rgba(255, 255, 255, 0), $color 70%) 0 100%,
    /* Shadows */ radial-gradient(50% 0, farthest-side, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)),
    radial-gradient(50% 100%, farthest-side, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) 0 100%;
  background:
		/* Shadow covers */ linear-gradient($color 30%, rgba(255, 255, 255, 0)),
    linear-gradient(rgba(255, 255, 255, 0), $color 70%) 0 100%,
    /* Shadows */ radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)),
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) 0 100%;

  background-repeat: no-repeat;
  background-color: $color;
  background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
  background-attachment: local, local, scroll, scroll;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 50px;

    &:hover {
      background: css-darken('border-color', 10%);
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  -ms-overflow-style: none;
}
</style>
